import * as React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useIsMobile } from 'hooks/use-mobile';
import { cn } from 'lib/utils';

interface ResponsiveTooltipProps {
	content: React.ReactNode;
	side?: 'top' | 'right' | 'bottom' | 'left';
	children?: React.ReactNode;
	className?: string;
}

/**
 * Uses Tooltip on desktop (hover) and Popover on mobile (tap).
 * If no children provided, renders an Info icon trigger.
 */
const ResponsiveTooltip = ({
	content,
	side = 'top',
	children,
	className
}: ResponsiveTooltipProps) => {
	const isMobile = useIsMobile();

	const trigger = children ?? (
		<button
			type="button"
			aria-label="Más información"
			className="relative before:absolute before:left-1/2 before:top-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 inline-flex items-center justify-center cursor-help"
		>
			<Info className="w-4 h-4 text-muted-foreground" />
		</button>
	);

	if (isMobile) {
		return (
			<Popover>
				<PopoverTrigger asChild>{trigger}</PopoverTrigger>
				<PopoverContent
					side={side}
					sideOffset={5}
					className={cn('max-w-xs text-xs p-3', className)}
				>
					{content}
				</PopoverContent>
			</Popover>
		);
	}

	return (
		<TooltipProvider delayDuration={100}>
			<Tooltip>
				<TooltipTrigger asChild>{trigger}</TooltipTrigger>
				<TooltipContent side={side} className={cn('max-w-xs text-xs', className)}>
					{content}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
};

export default ResponsiveTooltip;
